// Levi Voice — foreground Screen Wake Lock helper
// PO authorization 2026-08-07 (bounded implementation package B).
//
// This module owns nothing except a single navigator.wakeLock sentinel
// bound to the caller's existing conversation lifecycle. It does NOT
// track its own "conversation active" state. It does NOT retry on
// its own timer. It does NOT alter microphone, VAD, TTS, or auto-rearm.
// It is a thin, standards-based presentation-layer helper that keeps
// the screen awake while the CALLER indicates an active Levi Voice
// conversation, and releases the moment the caller says stop or the
// browser hides the tab.
//
// Contract (safe on every browser):
//   • isWakeLockSupported()  → boolean
//   • acquire(reason?)        → Promise<boolean> (true if held after call)
//   • release(reason?)        → Promise<void>
//   • isHeld()                → boolean
//
// Failure modes are ALL graceful:
//   • unsupported browser → acquire() returns false, no throw.
//   • rejected request     → acquire() returns false, no throw.
//   • sentinel auto-release on visibilitychange → tracked internally,
//     caller can re-acquire when it becomes appropriate again.
//
// No side effects on module load.

// PO 2026-08-07 diagnostic bounded round · emit lifecycle events into
// the shared getDiag ring buffer via `pushDiag` from leviSpeech. This
// import is diagnostic-only and does NOT change any acquire/release
// behavior of the wake-lock module. If the import ever fails at
// module-load time it must not break wake-lock or Levi Voice.
let _pushDiag = null;
try {
  // Lazy require guarded — leviSpeech is always present in the
  // Levi bundle, but wrap defensively so any future refactor
  // cannot regress this module.
  // eslint-disable-next-line no-inner-declarations
  import("./leviSpeech.js").then((m) => { _pushDiag = m.pushDiag || null; }).catch(() => { _pushDiag = null; });
} catch { _pushDiag = null; }
const _diag = (evt, data) => {
  try { if (typeof _pushDiag === "function") _pushDiag(evt, data || {}); } catch { /* diagnostic must never throw */ }
};

let _sentinel = null;
let _acquiring = false;

export function isWakeLockSupported() {
  try {
    return typeof navigator !== "undefined"
      && typeof navigator.wakeLock === "object"
      && navigator.wakeLock !== null
      && typeof navigator.wakeLock.request === "function";
  } catch {
    return false;
  }
}

export function isHeld() {
  try {
    return _sentinel != null && _sentinel.released === false;
  } catch {
    return false;
  }
}

export async function acquire(reason = "levi_voice_active") {
  if (!isWakeLockSupported()) {
    _diag("wakeLock:unsupported", { reason });
    return false;
  }
  // Guard against re-entry and duplicate holders.
  if (isHeld()) {
    _diag("wakeLock:duplicate", { reason, held: true });
    return true;
  }
  if (_acquiring) {
    _diag("wakeLock:duplicate", { reason, acquiring: true });
    return isHeld();
  }
  _acquiring = true;
  _diag("wakeLock:requested", { reason, visibilityState: (typeof document !== "undefined" ? document.visibilityState : null) });
  try {
    const sentinel = await navigator.wakeLock.request("screen");
    _sentinel = sentinel;
    _diag("wakeLock:acquired", { reason });
    // Browser may auto-release when document becomes hidden. Track it
    // so we do not falsely report the lock as held later.
    try {
      sentinel.addEventListener("release", () => {
        // Do NOT re-acquire here — reacquisition (if appropriate)
        // MUST be driven by the caller's existing lifecycle so we do
        // not create a background acquisition loop.
        if (_sentinel === sentinel) _sentinel = null;
        _diag("wakeLock:browser_release", { visibilityState: (typeof document !== "undefined" ? document.visibilityState : null) });
      });
    } catch { /* ignore addEventListener failure */ }
    try { console.log("[Levi WakeLock] acquired", reason); } catch { /* noop */ }
    return true;
  } catch (err) {
    const errName = String((err && (err.name || err.code)) || err || "unknown");
    _diag("wakeLock:rejected", { reason, error: errName });
    try { console.log("[Levi WakeLock] request rejected", reason, errName); } catch { /* noop */ }
    _sentinel = null;
    return false;
  } finally {
    _acquiring = false;
  }
}

export async function release(reason = "levi_voice_ended") {
  const s = _sentinel;
  _sentinel = null;
  if (s == null) {
    _diag("wakeLock:release_noop", { reason });
    return;
  }
  _diag("wakeLock:app_release", { reason });
  try {
    await s.release();
    try { console.log("[Levi WakeLock] released", reason); } catch { /* noop */ }
  } catch { /* ignore release errors */ }
}
